import type { RelayFieldLogger } from 'relay-runtime';

type FieldEvent = Parameters<RelayFieldLogger>[0];

/**
 * noise:  Relay reports every field with no value in the store as `missing_expected_data.log`,
 *         including client-only fields that are never fetched. It is not logged; it only marks a signal
 *         on the same field as missing from the store.
 * signal: logged with `isMissingFromStore`: true when a noise event for the same field was seen in the
 *         batch, false when the server returned an explicit null.
 * always: logged as is, with no `isMissingFromStore`.
 */
const EVENT_ROLES = {
	'missing_expected_data.log': 'noise',
	'missing_expected_data.throw': 'signal',
	'missing_required_field.log': 'signal',
	'missing_required_field.throw': 'signal',
	'relay_field_payload.error': 'always',
	'relay_resolver.error': 'always',
} satisfies Record<FieldEvent['kind'], 'noise' | 'signal' | 'always'>;

// A missing record, abstract type hint or module import is an incomplete store, not an unfetched field.
const STRUCTURAL_PATH = /(^|\.)<[\w-]+>$/;

// Bounds memory during a long synchronous render pass, where one batch can hold every event of a page.
const MAX_MISSING_FIELDS = 1000;

const getRole = (event: FieldEvent): 'noise' | 'signal' | 'always' =>
	EVENT_ROLES[event.kind] === 'noise' && STRUCTURAL_PATH.test(event.fieldPath)
		? 'always'
		: EVENT_ROLES[event.kind];

// List indexes are normalised so repeated list items count as one field.
const getFieldKey = (event: FieldEvent): string =>
	`${event.owner}|${event.fieldPath.replace(/(^|\.)\d+(?=\.|$)/g, '$1N')}`;

const getEventKey = (event: FieldEvent): string =>
	event.kind === 'relay_resolver.error'
		? `${event.kind}|${getFieldKey(event)}|${event.error?.message}`
		: `${event.kind}|${getFieldKey(event)}`;

/**
 * Relay logs every field event of one read in a single synchronous loop and only then throws, so a
 * microtask flush sees the whole batch. Events are logged once per batch. For signals,
 * `isMissingFromStore` tells whether the field had no value in the store (true) or the server returned
 * an explicit null (false); it is null for other events, where the distinction does not apply.
 */
export const correlateFieldEvents = (
	emit: (event: FieldEvent, isMissingFromStore: boolean | null) => void,
	schedule: (flush: () => void) => void = queueMicrotask,
): RelayFieldLogger => {
	let events = new Map<string, FieldEvent>();
	let missingFields = new Set<string>();
	let isScheduled = false;

	const flush = () => {
		const batch = events;
		const missing = missingFields;
		events = new Map();
		missingFields = new Set();
		isScheduled = false;

		for (const event of batch.values()) {
			emit(event, EVENT_ROLES[event.kind] === 'signal' ? missing.has(getFieldKey(event)) : null);
		}
	};

	return (event) => {
		if (getRole(event) === 'noise') {
			if (missingFields.size < MAX_MISSING_FIELDS) {
				missingFields.add(getFieldKey(event));
			}
		} else {
			const eventKey = getEventKey(event);
			if (!events.has(eventKey)) {
				events.set(eventKey, event);
			}
		}

		if (!isScheduled) {
			isScheduled = true;
			schedule(flush);
		}
	};
};
